
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { SocialMediaProfileDisplay } from '@/components/profile/SocialMediaProfileDisplay';
import { UnifiedTeamsSection } from '@/components/profile/UnifiedTeamsSection';
import { ProfileTrainingSessions } from '@/components/profile/ProfileTrainingSessions';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import ErrorBoundary from '@/components/ErrorBoundary';
import { FeaturedSpotlight } from '@/components/featured/FeaturedSpotlight';
import { SocialMediaEmbeds } from '@/components/social/SocialMediaEmbeds';
import { useAuth } from '@/hooks/useAuth';

const UserProfile = () => {
  const { userSlug } = useParams<{ userSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, userTeams, loading } = useUserProfileData(userSlug);
  const isOwnProfile = user?.id === profile?.id;

  // Redirect venue users to VenueProfile component for unified experience
  useEffect(() => {
    if (profile && profile.role === 'venue') {
      console.log('Redirecting venue user to VenueProfile:', userSlug);
      navigate(`/venue/${userSlug}`, { replace: true });
    }
  }, [profile, userSlug, navigate]);

  console.log('=== UserProfile Component Debug ===');
  console.log('UserProfile - userSlug:', userSlug);
  console.log('UserProfile - profile:', profile);
  console.log('UserProfile - userTeams:', userTeams);
  console.log('UserProfile - loading:', loading);
  console.log('=== End Debug ===');

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-black">
        <div className="hidden md:block">
          <Header />
        </div>
        <div className="block md:hidden">
          <MobileHeader />
        </div>
        <main className="flex-grow pt-20 flex items-center justify-center">
          <div className="text-white text-xl">Loading profile...</div>
        </main>
        <Footer />
        <MobileBottomNav />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col min-h-screen bg-black">
        <div className="hidden md:block">
          <Header />
        </div>
        <div className="block md:hidden">
          <MobileHeader />
        </div>
        <main className="flex-grow pt-20 flex items-center justify-center">
          <div className="text-white text-xl">User not found</div>
        </main>
        <Footer />
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-black">
      <div className="hidden md:block">
        <Header />
      </div>
      <div className="block md:hidden">
        <MobileHeader />
      </div>
      
      <main className="flex-grow pt-20 py-6 md:py-12 px-4 md:px-6 pb-20 md:pb-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <ErrorBoundary>
            {/* Featured Spotlight */}
            {profile.role && ['coach', 'player'].includes(profile.role) && (
              <FeaturedSpotlight 
                itemType={profile.role as 'coach' | 'player'} 
                itemId={profile.id} 
                showPurchase={isOwnProfile}
              />
            )}

            <SocialMediaProfileDisplay profile={profile} />
            
            {/* Social Media Embeds */}
            <SocialMediaEmbeds 
              userId={profile.id}
              userType={profile.role as 'coach' | 'team_owner' | 'venue' | 'player'}
              editable={isOwnProfile}
            />

            {/* Training Sessions - for coaches */}
            {profile.role === 'coach' && (
              <ProfileTrainingSessions 
                userId={profile.id} 
                username={profile.slug || profile.username || ''} 
              />
            )}

            {/* Teams Section */}
            <UnifiedTeamsSection userTeams={userTeams} />
          </ErrorBoundary>
        </div>
      </main>
      
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default UserProfile;
